// Agent Portal (/cti2) — reload guard. While a call-centre agent is logged
// in to a telephony profile (WebRTC / Soft Phone / Mobile), F5 / Ctrl+R /
// Cmd+R open ReloadGuardDialog instead of reloading. The dialog has no way to
// leave: the agent logs out of the profile first if they really need to reload.
//
// The browser's reload button, tab close and browser close can't show custom
// UI, so beforeunload asks for the browser's own "Leave site?" prompt there.
//
// "Logged in to a profile" = sessionStorage.activeLoginType, the same key
// Dialer.tsx's profile picker shows as connected (written by
// loginAsProfile/logoutAsProfile via persistActiveLoginType). Everything is
// read at event time, not from React state: every app-driven logout /
// session-expiry / force-logout path either clears that key (or all
// storage) or switches back to a non-agent user before it navigates, so
// none of them trip the prompt.
//
// While a call/disposition blocks exit, ExitGuard.tsx owns the reload keys
// (its stricter "can't leave yet" dialog) — this guard steps aside so the two
// never open together. Its beforeunload still prompts (harmless: the browser
// shows one prompt).
//
import { useCallback, useEffect, useState } from 'react';
import { ACTIVE_LOGIN_TYPE_KEY, getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { isExitBlocked } from '@/router/zustand/callStore';

const GUARD_CLASS = 'agent-reload-guard';

const isLoggedInToProfile = () => {
  try { return Boolean(sessionStorage.getItem(ACTIVE_LOGIN_TYPE_KEY)); } catch { return false; }
};

const isGuardActive = () =>
  getUser()?.role === USER_ROLES.CALL_CENTRE_AGENT && isLoggedInToProfile();

const isReloadKey = (e: KeyboardEvent) =>
  e.key === 'F5' ||
  ((e.ctrlKey || e.metaKey) && (e.code === 'KeyR' || String(e.key).toLowerCase() === 'r'));

export function useReloadGuard() {
  const [active, setActive] = useState(isGuardActive);
  const [open, setOpen] = useState(false);

  // Follow profile login/logout (loginAsProfile dispatches sessionStorageChange).
  useEffect(() => {
    const sync = () => setActive(isGuardActive());
    window.addEventListener('sessionStorageChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('sessionStorageChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isReloadKey(e) || !isGuardActive() || isExitBlocked()) return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
    };
    // Browsers only show their own generic text here; custom strings are ignored.
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isGuardActive()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  // Stops Chrome Android pull-to-refresh while logged in (see cti2.css).
  useEffect(() => {
    if (!active) return undefined;
    document.documentElement.classList.add(GUARD_CLASS);
    document.body.classList.add(GUARD_CLASS);
    return () => {
      document.documentElement.classList.remove(GUARD_CLASS);
      document.body.classList.remove(GUARD_CLASS);
    };
  }, [active]);

  const stay = useCallback(() => setOpen(false), []);

  return { open, stay };
}
